import { ChangeDetectorRef, Component, Inject, Injector, OnDestroy, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { NodeType } from '@completion/enums';
import { CheckTestContent, DocumentNode, ProcedureDocument } from '@completion/models';
import { selectEditMode, getCurrentDocument } from '@completion/selectors';
import { NodeFactoryService } from '../../node-factory.service';
import { DocumentUpdateQueueService } from '../../../../core/services/document-update-queue.service';

@Component({
  selector: 'app-test-section',
  templateUrl: './test-section.component.html',
  styleUrls: ['./test-section.component.scss'],
  standalone: false,
})
export class TestSectionComponent implements OnInit, OnDestroy {
  nodes: DocumentNode[] = [];
  heading = '';
  chapterNumber = '';
  editMode = false;
  editingHeading = false;
  headingEditControl = new FormControl('');
  originalHeading = '';

  NodeType = NodeType;
  private readonly destroy$ = new Subject<void>();
  private static nextId = -1;

  constructor(
    public nodeFactory: NodeFactoryService,
    public injector: Injector,
    @Inject('documentNode') public documentNode: DocumentNode,
    @Inject('node') public parentDocument: ProcedureDocument,
    private store: Store,
    private updateQueue: DocumentUpdateQueueService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.nodes = this.documentNode.children || [];
    this.heading = this.documentNode.name || 'Checks / Tests';

    this.store
      .select(selectEditMode)
      .pipe(takeUntil(this.destroy$))
      .subscribe((m) => (this.editMode = m));

    this.store
      .select(getCurrentDocument)
      .pipe(takeUntil(this.destroy$))
      .subscribe((doc) => {
        if (!doc) {
          return;
        }
        this.chapterNumber = this.computeChapterNumber(doc.nodes || [], this.documentNode.id);
        const updated = this.findNodeById(doc.nodes || [], this.documentNode.id);
        if (updated) {
          this.nodes = updated.children || [];
          if (!this.editingHeading) {
            this.heading = updated.name || 'Checks / Tests';
          }
          try {
            this.cdr.markForCheck();
          } catch {}
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Walk the document node tree to compute a hierarchical chapter number
   * for the section with the given targetId.
   */
  private computeChapterNumber(nodes: DocumentNode[], targetId: number, prefix: string = ''): string {
    let counter = 0;
    for (const node of nodes) {
      if (node.type === NodeType.SECTION || node.type === NodeType.TEST_SECTION) {
        counter++;
        const num = prefix ? `${prefix}.${counter}` : `${counter}`;
        if (node.id === targetId) {
          return num;
        }
        if (node.children?.length) {
          const found = this.computeChapterNumber(node.children, targetId, num);
          if (found) {
            return found;
          }
        }
      }
    }
    return '';
  }

  // ── Heading editing ────────────────────────────────────────────────────────

  startEditHeading(): void {
    if (!this.editMode) {
      return;
    }
    this.editingHeading = true;
    this.originalHeading = this.heading;
    this.headingEditControl.setValue(this.heading);
  }

  cancelEditHeading(): void {
    this.editingHeading = false;
    this.headingEditControl.setValue(this.originalHeading);
  }

  saveEditHeading(): void {
    const val = (this.headingEditControl.value || '').trim();
    if (!val) {
      this.cancelEditHeading();
      return;
    }
    this.heading = val;
    this.editingHeading = false;
    this.persistSectionUpdate((section) => ({ ...section, name: val }));
  }

  // ── Add / remove check-test rows ──────────────────────────────────────────

  addCheckTest(): void {
    const emptyContent: CheckTestContent = { name: '', executorCompany: null, sign: null, comments: '' };
    const newNode: DocumentNode = {
      id: TestSectionComponent.nextId--,
      content: JSON.stringify(emptyContent),
      name: 'Check/Test',
      type: NodeType.PROCEDURE_CHECK,
      children: [],
    };
    this.persistSectionUpdateImmediate((section) => {
      const children = [...(section.children || []), newNode].map((c, i) => ({ ...c, ordinal: i }));
      return { ...section, children };
    });
  }

  removeCheckTest(nodeId: number): void {
    this.persistSectionUpdateImmediate((section) => {
      const children = (section.children || []).filter((c) => c.id !== nodeId).map((c, i) => ({ ...c, ordinal: i }));
      return { ...section, children };
    });
  }

  removeSection(): void {
    this.store
      .select(getCurrentDocument)
      .pipe(take(1))
      .subscribe((doc) => {
        if (!doc) {
          return;
        }
        const updatedNodes = (doc.nodes || []).filter((n) => n.id !== this.documentNode.id);
        this.updateQueue.queueImmediate({ ...doc, nodes: updatedNodes });
      });
  }

  // ── Helpers ────────────────────────────────────────────────────────────────

  private persistSectionUpdate(transform: (section: DocumentNode) => DocumentNode): void {
    this.store
      .select(getCurrentDocument)
      .pipe(take(1))
      .subscribe((doc) => {
        if (!doc) {
          return;
        }
        const updatedNodes = this.replaceNode(doc.nodes || [], this.documentNode.id, transform);
        this.updateQueue.queue({ ...doc, nodes: updatedNodes });
      });
  }

  /** Like persistSectionUpdate but bypasses debounce — use for structural changes (add/remove rows). */
  private persistSectionUpdateImmediate(transform: (section: DocumentNode) => DocumentNode): void {
    this.store
      .select(getCurrentDocument)
      .pipe(take(1))
      .subscribe((doc) => {
        if (!doc) {
          return;
        }
        const updatedNodes = this.replaceNode(doc.nodes || [], this.documentNode.id, transform);
        this.updateQueue.queueImmediate({ ...doc, nodes: updatedNodes });
      });
  }

  private replaceNode(nodes: DocumentNode[], id: number, transform: (n: DocumentNode) => DocumentNode): DocumentNode[] {
    return nodes.map((n) => {
      if (n.id === id) {
        return transform(n);
      }
      if (n.children?.length) {
        const updated = this.replaceNode(n.children, id, transform);
        if (updated !== n.children) {
          return { ...n, children: updated };
        }
      }
      return n;
    });
  }

  private findNodeById(nodes: DocumentNode[], id: number): DocumentNode | null {
    for (const n of nodes) {
      if (n.id === id) {
        return n;
      }
      if (n.children?.length) {
        const found = this.findNodeById(n.children, id);
        if (found) {
          return found;
        }
      }
    }
    return null;
  }
}
